import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import OpenAPI from 'dashboard/api/integrations/openapi';

const shownSuggestions = new Set();
const pendingSuggestions = new Set();

const isPortugueseLocale = locale =>
  String(locale || '')
    .toLowerCase()
    .startsWith('pt');

const containsProduct = (cart, productName) => {
  const normalized = String(productName || '').toLowerCase();
  return cart.some(item =>
    String(item?.name || '')
      .toLowerCase()
      .includes(normalized)
  );
};

const suggestionKey = (conversationId, ruleId) => `${conversationId}-${ruleId}`;

const parseUpsellResponse = rawResponse => {
  if (!rawResponse || rawResponse === 'NO_SUGGESTION') return null;

  try {
    const parsed = JSON.parse(rawResponse);
    if (!parsed?.message || !parsed?.messageRobot) return null;
    return {
      message: String(parsed.message).trim(),
      messageRobot: String(parsed.messageRobot).trim(),
    };
  } catch {
    return {
      message: String(rawResponse).trim(),
      messageRobot: String(rawResponse).trim(),
    };
  }
};

const buildUpsellRobotPayload = ({ locale, suggestion }) => {
  const isPt = isPortugueseLocale(locale);

  return {
    message: suggestion.messageRobot,
    buttons: [
      {
        id: 'acceptUpsellSuggestion',
        label: isPt ? 'Aceitar sugestao' : 'Accept suggestion',
        variant: 'primary',
        eventName: BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR,
        payload: suggestion.message,
        dismissBubble: true,
      },
      {
        id: 'dismissUpsellSuggestion',
        label: isPt ? 'Dispensar' : 'Dismiss',
        variant: 'secondary',
        eventName: BUS_EVENTS.HIDE_ASSISTANT_ROBOT,
        dismissBubble: true,
      },
    ],
  };
};

const emitUpsellSuggestion = robotPayload => {
  emitter.emit(BUS_EVENTS.SHOW_ASSISTANT_ROBOT, {
    ...robotPayload,
  });
};

const buildUpsellInput = ({ cart, locale }) => {
  const cartItems = cart
    .map(item => String(item?.name || '').trim())
    .filter(Boolean);
  return JSON.stringify({
    locale: locale || 'pt-BR',
    cart_items: cartItems,
  });
};

const fetchUpsellSuggestion = async ({
  cart,
  hookId,
  conversationId,
  locale,
}) => {
  const response = await OpenAPI.processEvent({
    hookId,
    conversationId,
    type: 'upsell_suggestion',
    content: buildUpsellInput({ cart, locale }),
  });

  return response?.data?.message?.trim();
};

export const triggerUpsellAgent = async ({
  cart = [],
  conversationId,
  hookId,
  locale,
}) => {
  if (!Array.isArray(cart) || !cart.length || !conversationId || !hookId) {
    return false;
  }

  // First rule: Dipirona -> Dorflex
  const ruleId = 'dipirona-dorflex';
  if (!containsProduct(cart, 'dipirona')) return false;

  const key = suggestionKey(conversationId, ruleId);
  if (shownSuggestions.has(key) || pendingSuggestions.has(key)) return false;

  pendingSuggestions.add(key);

  try {
    const aiReplyMessage = await fetchUpsellSuggestion({
      cart,
      hookId,
      conversationId,
      locale,
    });

    const suggestion = parseUpsellResponse(aiReplyMessage);
    if (!suggestion?.message || !suggestion?.messageRobot) {
      return false;
    }

    shownSuggestions.add(key);
    const robotPayload = buildUpsellRobotPayload({ locale, suggestion });
    emitUpsellSuggestion(robotPayload);
    return true;
  } catch (error) {
    return false;
  } finally {
    pendingSuggestions.delete(key);
  }
};

export const resetUpsellAgentForConversation = conversationId => {
  if (!conversationId) return;
  [...shownSuggestions]
    .filter(key => key.startsWith(`${conversationId}-`))
    .forEach(key => shownSuggestions.delete(key));
  [...pendingSuggestions]
    .filter(key => key.startsWith(`${conversationId}-`))
    .forEach(key => pendingSuggestions.delete(key));
};
